// src/hr/useHrMe.js
// Loads GET /api/hr/me once per mount and never throws.
//   status 'loading'     -> first load in flight
//          'ready'       -> me holds the answer
//          'unavailable' -> 404 or no answer at all (the HR API isn't running yet): show a calm
//                           "being set up" state and hide HR-only entry points
//          'error'       -> any other failure (403, 500, ...)
// reload() fetches again and keeps the current data on screen until the new answer arrives.
// useHrMe({ enabled: false }) makes no request and returns status 'idle' (reload is a no-op);
// it starts loading when enabled turns true. Used by the menu and homepage for signed-out and
// pending users, who must never call the HR API.
import { useCallback, useEffect, useRef, useState } from 'react';
import { hr } from './api';

function failureStatus(err) {
  const status = err && err.response && err.response.status;
  if (!status || status === 404) return 'unavailable';
  return 'error';
}

const IDLE = { status: 'idle', me: null };

export function useHrMe({ enabled = true } = {}) {
  const [state, setState] = useState(enabled ? { status: 'loading', me: null } : IDLE);
  const mounted = useRef(true);
  const latest = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const load = useCallback(async () => {
    if (!enabledRef.current) return;
    latest.current += 1;
    const call = latest.current;
    // The first load after being enabled shows 'loading'; a reload keeps the current answer on screen.
    setState((s) => (s.status === 'idle' ? { status: 'loading', me: null } : s));
    let next;
    try {
      const me = await hr.me();
      next = { status: 'ready', me: me || null };
    } catch (err) {
      next = { status: failureStatus(err), me: null };
    }
    // Ignore answers that arrive after unmount or after a newer reload started.
    if (mounted.current && call === latest.current) setState(next);
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (enabled) {
      load();
    } else {
      latest.current += 1; // drop any answer still in flight
      setState(IDLE);
    }
    return () => { mounted.current = false; };
  }, [enabled, load]);

  if (!enabled) return { status: IDLE.status, me: IDLE.me, reload: load };
  return { status: state.status, me: state.me, reload: load };
}

export default useHrMe;
